import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Zap, FileText, CheckCircle2, ShieldCheck, Clock, X, PartyPopper,
  ArrowRight, Calendar, AlertTriangle, DollarSign, ChevronLeft, Sparkles, Building2,
} from 'lucide-react';

/**
 * QuickDrawPreview — CLICKABLE PROTOTYPE (no backend, fake data).
 * Demonstrates the proposed fast "Quick Draw" flow + the 24h blocking
 * deposit-confirmation modal. Nothing here touches real projects.
 * Route: /quick-draw-preview
 */

const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().split('T')[0];

const PROJECT = {
  name: 'Pre-Sold DW Single Family',
  address: '3321 Sokol Ln',
  loan: 368960,
  drawn: 58630.45,
};

const ITEMS = [
  { id: 'framing', name: 'Framing', category: 'Labor & Materials', balance: 18240, coi: true, lien: true },
  { id: 'foundation', name: 'Foundation', category: 'Concrete', balance: 12500, coi: true, lien: true },
  { id: 'electrical', name: 'Electrical Rough-In', category: 'Labor', balance: 8900, coi: true, lien: true },
  { id: 'fixtures', name: 'Cabinet Hardware', category: 'Materials', balance: 1820, coi: false, lien: false },
];

export default function QuickDrawPreview() {
  const [stage, setStage] = useState('home'); // home | quickdraw | submitted | reminder | recorded | snoozed | discarded
  const [selectedId, setSelectedId] = useState(null);
  const [amount, setAmount] = useState('');
  const [depositDate, setDepositDate] = useState(today());

  const item = ITEMS.find((i) => i.id === selectedId) || null;
  const available = PROJECT.loan - PROJECT.drawn;
  const drawAmt = parseFloat(amount) || 0;

  const openQuickDraw = () => { setSelectedId(null); setAmount(''); setStage('quickdraw'); };
  const pick = (i) => { setSelectedId(i.id); setAmount(String(i.balance)); };
  const submit = () => setStage('submitted');
  const startOver = () => { setStage('home'); setSelectedId(null); setAmount(''); setDepositDate(today()); };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Prototype banner */}
      <div className="bg-amber-100 border-b border-amber-200 text-amber-900 text-center text-xs py-2 px-4" data-testid="prototype-banner">
        <Sparkles className="w-3.5 h-3.5 inline mr-1" />
        Clickable prototype · fake data · nothing here affects real projects
      </div>

      {/* Brand header */}
      <div className="bg-gradient-to-r from-blue-700 to-cyan-600 text-white px-6 py-5">
        <p className="text-xs uppercase tracking-widest text-cyan-100">DrawReady · Proposed flow</p>
        <h1 className="text-2xl font-bold flex items-center gap-2 mt-1">
          <Zap className="w-6 h-6" /> Quick Draw
        </h1>
        <p className="text-sm text-cyan-50/90 mt-1">Fewer clicks to submit · auto-attached compliance · a nudge so you never forget to confirm the deposit.</p>
      </div>

      <div className="max-w-2xl mx-auto p-6">
        {/* ── HOME ─────────────────────────────────────── */}
        {stage === 'home' && (
          <div className="space-y-5" data-testid="stage-home">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                <Building2 className="w-4 h-4" /> {PROJECT.name}
              </div>
              <p className="text-lg font-semibold text-slate-800">{PROJECT.address}</p>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <Stat label="Total Loan" value={money(PROJECT.loan)} />
                <Stat label="Amount Drawn" value={money(PROJECT.drawn)} />
                <Stat label="Available to Draw" value={money(available)} accent />
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 space-y-3">
              <p className="text-sm font-semibold text-slate-700">Start a draw</p>
              <button
                onClick={openQuickDraw}
                data-testid="quick-draw-btn"
                className="w-full group relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-5 py-4 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40 transition-all hover:-translate-y-0.5"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <p className="font-bold text-lg flex items-center gap-2"><Zap className="w-5 h-5" /> Quick Draw</p>
                    <p className="text-xs text-cyan-50/90">One item · amount pre-filled · docs auto-attached · ~2 clicks</p>
                  </div>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>

              <button
                data-testid="full-draw-btn"
                className="w-full rounded-xl border border-slate-200 bg-white px-5 py-3 text-left hover:bg-slate-50 transition-colors"
              >
                <p className="font-semibold text-slate-700 flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" /> New Draw Request (full)</p>
                <p className="text-xs text-slate-500">Multi-phase draw with the full request builder</p>
              </button>
              <p className="text-[11px] text-emerald-600 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Fix included: this button no longer bounces you to the Loan Details page.
              </p>
            </div>
          </div>
        )}

        {/* ── QUICK DRAW MODAL ─────────────────────────── */}
        {stage === 'quickdraw' && (
          <Overlay onClose={startOver} testid="stage-quickdraw">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-slate-800">Quick Draw</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">Pick the item you're drawing for. We'll pre-fill the amount and attach the compliance docs automatically.</p>

            <p className="text-xs font-semibold text-slate-600 mb-2">What are you drawing for?</p>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {ITEMS.map((i) => {
                const active = selectedId === i.id;
                return (
                  <button
                    key={i.id}
                    onClick={() => pick(i)}
                    data-testid={`quick-draw-item-${i.id}`}
                    className={`w-full text-left rounded-xl border px-4 py-3 transition-all ${active ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-800">{i.name}</p>
                        <p className="text-[11px] text-slate-500">{i.category}</p>
                      </div>
                      <p className="font-semibold text-slate-700">{money(i.balance)}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {item && (
              <div className="mt-4 space-y-4 animate-in fade-in duration-200">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Draw amount</label>
                  <div className="relative mt-1">
                    <DollarSign className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      data-testid="quick-draw-amount"
                      className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                    />
                  </div>
                </div>

                {/* Compliance auto-attached */}
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Compliance auto-attached</p>
                    <span className="text-[11px] font-bold text-white bg-emerald-600 rounded-full px-2 py-0.5">DrawReady 100</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Chip ok>Invoice</Chip>
                    <Chip ok={item.coi}>{item.coi ? 'COI' : 'COI skipped (<$2,500)'}</Chip>
                    <Chip ok={item.lien}>{item.lien ? 'Lien Waiver' : 'Lien skipped (<$2,500)'}</Chip>
                  </div>
                  <p className="text-[11px] text-emerald-700/80 mt-2">No selecting docs — we pull what's already on file for this item.</p>
                </div>

                <button
                  onClick={submit}
                  data-testid="quick-draw-submit"
                  className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 flex items-center justify-center gap-2 transition-colors"
                >
                  Submit Draw Request <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-[11px] text-center text-slate-400 flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" /> We'll remind you in 24 hours to confirm the deposit.
                </p>
              </div>
            )}
          </Overlay>
        )}

        {/* ── SUBMITTED ────────────────────────────────── */}
        {stage === 'submitted' && (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 text-center space-y-4" data-testid="stage-submitted">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center animate-in zoom-in duration-300">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Draw request submitted! 🎉</h2>
              <p className="text-sm text-slate-500 mt-1">{money(drawAmt)} · {item?.name} · {PROJECT.address}</p>
            </div>
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-left">
              <p className="text-xs font-semibold text-blue-800 flex items-center gap-1"><Clock className="w-4 h-4" /> Pending deposit confirmation</p>
              <p className="text-[12px] text-blue-700/90 mt-1">A pending draw was created automatically. We'll nudge you by email + an in-app popup in 24h so you never forget to confirm — and interest only starts once you tell us the money actually landed.</p>
            </div>
            <button
              onClick={() => setStage('reminder')}
              data-testid="simulate-24h-btn"
              className="w-full rounded-xl border-2 border-dashed border-slate-300 text-slate-600 py-2.5 text-sm hover:bg-slate-50 transition-colors"
            >
              ⏱ Simulate 24 hours later →
            </button>
            <button onClick={startOver} className="text-xs text-slate-400 hover:text-slate-600">Start over</button>
          </div>
        )}

        {/* ── 24h BLOCKING REMINDER MODAL ──────────────── */}
        {stage === 'reminder' && (
          <BlockingModal testid="stage-reminder">
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-3 animate-pulse">
                <DollarSign className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Did your draw get deposited?</h2>
              <p className="text-sm text-slate-500 mt-1">{money(drawAmt)} draw for <b>{PROJECT.address}</b> — submitted yesterday.</p>
            </div>

            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Calendar className="w-4 h-4 text-blue-600" /> When did the funds hit your account?
              </label>
              <p className="text-[11px] text-slate-500 mb-2">Required — this is the exact date interest starts accruing, so your balance stays accurate.</p>
              <input
                type="date"
                value={depositDate}
                onChange={(e) => setDepositDate(e.target.value)}
                data-testid="deposit-date-input"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
              />
            </div>

            <div className="mt-5 space-y-2">
              <button
                onClick={() => setStage('recorded')}
                data-testid="confirm-deposit-btn"
                className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 flex items-center justify-center gap-2 transition-colors"
              >
                <CheckCircle2 className="w-5 h-5" /> Yes — record this draw
              </button>
              <button
                onClick={() => setStage('snoozed')}
                data-testid="snooze-btn"
                className="w-full rounded-xl border border-slate-300 text-slate-700 py-2.5 text-sm hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
              >
                <Clock className="w-4 h-4" /> Not yet — remind me tomorrow
              </button>
              <button
                onClick={() => setStage('discarded')}
                data-testid="discard-btn"
                className="w-full text-red-500 hover:text-red-700 py-1.5 text-xs flex items-center justify-center gap-1"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> It fell through — discard this draw
              </button>
            </div>
          </BlockingModal>
        )}

        {/* ── RECORDED ─────────────────────────────────── */}
        {stage === 'recorded' && (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 text-center space-y-4" data-testid="stage-recorded">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center animate-in zoom-in duration-300">
              <PartyPopper className="w-9 h-9 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Recorded! 🎯</h2>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-left space-y-2">
              <Row label="Amount Drawn" from={money(PROJECT.drawn)} to={money(PROJECT.drawn + drawAmt)} />
              <Row label="Available to Draw" from={money(available)} to={money(available - drawAmt)} />
              <div className="flex items-center gap-1 text-[12px] text-blue-700 pt-1">
                <Calendar className="w-3.5 h-3.5" /> Interest now accruing from {depositDate}
              </div>
            </div>
            <p className="text-xs text-slate-500">All of that happened from one tap — no re-entering amounts, no forgetting.</p>
            <button onClick={startOver} data-testid="restart-btn" className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 flex items-center justify-center gap-2">
              <ChevronLeft className="w-4 h-4" /> Run the flow again
            </button>
          </div>
        )}

        {/* ── SNOOZED / DISCARDED ──────────────────────── */}
        {(stage === 'snoozed' || stage === 'discarded') && (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 text-center space-y-4" data-testid={`stage-${stage}`}>
            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${stage === 'snoozed' ? 'bg-amber-100' : 'bg-red-100'}`}>
              {stage === 'snoozed' ? <Clock className="w-9 h-9 text-amber-600" /> : <X className="w-9 h-9 text-red-600" />}
            </div>
            <h2 className="text-xl font-bold text-slate-800">{stage === 'snoozed' ? "Okay — we'll remind you tomorrow" : 'Pending draw discarded'}</h2>
            <p className="text-sm text-slate-500">
              {stage === 'snoozed'
                ? 'The popup returns in 24h until you confirm the deposit. Nothing is recorded and no interest accrues yet.'
                : 'No draw was recorded and nothing changed on your loan. You can start a new draw anytime.'}
            </p>
            <button onClick={startOver} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3">Start over</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-xl p-3 ${accent ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-slate-200'}`}>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-sm font-bold ${accent ? 'text-blue-700' : 'text-slate-800'}`}>{value}</p>
    </div>
  );
}

function Chip({ children, ok }) {
  return (
    <span className={`text-[11px] rounded-full px-2 py-0.5 border inline-flex items-center gap-1 ${ok ? 'bg-white border-emerald-300 text-emerald-700' : 'bg-slate-100 border-slate-200 text-slate-500'}`}>
      {ok && <CheckCircle2 className="w-3 h-3" />}{children}
    </span>
  );
}

function Row({ label, from, to }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="flex items-center gap-1.5">
        <span className="text-slate-400 line-through text-xs">{from}</span>
        <ArrowRight className="w-3 h-3 text-slate-400" />
        <span className="font-semibold text-slate-800">{to}</span>
      </span>
    </div>
  );
}

function Overlay({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" data-testid={testid}>
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl p-5">
        <button onClick={onClose} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600" data-testid="overlay-close">
          <X className="w-5 h-5" />
        </button>
        {children}
      </div>
    </div>
  );
}

// Non-dismissible (no backdrop click / no close X) — the "you must address it" modal.
function BlockingModal({ children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/75" data-testid={testid}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-blue-200 p-6">
        {children}
      </div>
    </div>
  );
}
